import React, { useState, useRef } from "react";
import http from "../../Utils/axiosConfig";
import { apiMessage } from "../../Utils/apiMessage";
import "./BulkUpload.css";

const MODES = {
  members:    { label: "सभासद नोंदणी",  endpoint: "bulk-upload/members",    accept: ".csv" },
  milkEntry:  { label: "दूध नोंद",       endpoint: "bulk-upload/milk-entry", accept: ".csv" },
};

const SAMPLE_LINKS = {
  members:   "/sample-csv/bulk-members-sample.csv",
  milkEntry: "/sample-csv/bulk-milk-entry-sample.csv",
};

const fmt = (v) => (v != null ? Number(v).toFixed(2) : "-");

// ₹ change for a preview row: full amount for a new entry, delta for an update.
const rowDiff = (r) => (r.action === "update" ? r.after.total_rate - r.before.total_rate : r.after.total_rate);

const ACTION_LABEL = {
  insert: { text: "नवीन", cls: "bu-action-insert" },
  update: { text: "बदलणार", cls: "bu-action-update" },
  failed: { text: "अयशस्वी", cls: "bu-action-failed" },
};

const BulkUpload = () => {
  const [mode, setMode]         = useState("members");
  const [file, setFile]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [preview, setPreview]   = useState(null); // milk-entry only: preview before commit
  const [error, setError]       = useState("");
  const fileRef                 = useRef();

  const isMilkEntry = mode === "milkEntry";

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".csv")) {
      setError("फक्त CSV फाइल स्वीकारली जाते.");
      setFile(null);
      return;
    }
    setFile(f);
    setError("");
    setResult(null);
    setPreview(null);
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  };

  // Members mode: single-step direct upload, unchanged.
  const handleUploadMembers = async () => {
    if (!file) { setError("कृपया CSV फाइल निवडा."); return; }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await http.post(MODES[mode].endpoint, buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.code === "BULK_UPLOAD_DONE") {
        setResult(res.data);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        setError(apiMessage(res, "अपलोड अयशस्वी झाले."));
      }
    } catch (e) {
      console.error("Bulk upload error:", e);
      setError(apiMessage());
    } finally {
      setLoading(false);
    }
  };

  // Milk-entry mode: preview first (no DB writes), admin approves, then commit.
  const handlePreview = async () => {
    if (!file) { setError("कृपया CSV फाइल निवडा."); return; }
    setLoading(true);
    setError("");
    setResult(null);
    setPreview(null);
    try {
      const res = await http.post(`${MODES[mode].endpoint}/preview`, buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.code === "BULK_UPLOAD_PREVIEW_READY") {
        setPreview(res.data);
      } else {
        setError(apiMessage(res, "पूर्वावलोकन अयशस्वी झाले."));
      }
    } catch (e) {
      console.error("Bulk upload preview error:", e);
      setError(apiMessage());
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!file || preview?.hasDuplicates) return;
    setLoading(true);
    setError("");
    try {
      const res = await http.post(`${MODES[mode].endpoint}/commit`, buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.code === "BULK_UPLOAD_DONE") {
        setResult(res.data);
        setPreview(null);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        setError(apiMessage(res, "अपलोड अयशस्वी झाले."));
      }
    } catch (e) {
      console.error("Bulk upload confirm error:", e);
      setError(apiMessage());
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="bu-container">
      <div className={`bu-box ${preview ? "bu-box--wide" : ""}`}>
        <h2 className="bu-title">📤 Bulk Upload</h2>

        {/* Mode selector */}
        <div className="bu-mode-tabs">
          {Object.entries(MODES).map(([key, val]) => (
            <button
              key={key}
              className={`bu-mode-tab ${mode === key ? "bu-mode-tab--active" : ""}`}
              onClick={() => { setMode(key); handleReset(); }}
            >
              {val.label}
            </button>
          ))}
        </div>

        {!preview && (
          <>
            {/* Instructions */}
            <div className="bu-info">
              <p>CSV फाइल अपलोड करा. स्तंभांचा क्रम नमुना फाइलप्रमाणे असावा.</p>
              {isMilkEntry && (
                <p>आधीपासून अस्तित्वात असलेली नोंद (सभासद+तारीख+सत्र) आढळल्यास ती अपडेट होईल — मंजुरीपूर्वी बदल दाखवले जातील.</p>
              )}
              <a
                href={SAMPLE_LINKS[mode]}
                download
                className="bu-sample-link"
                target="_blank"
                rel="noreferrer"
              >
                📥 नमुना CSV डाउनलोड करा
              </a>
            </div>

            {/* File input */}
            <div className="bu-file-area" onClick={() => fileRef.current?.click()}>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              {file ? (
                <div className="bu-file-selected">
                  <span className="bu-file-icon">📄</span>
                  <span className="bu-file-name">{file.name}</span>
                  <span className="bu-file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
              ) : (
                <div className="bu-file-placeholder">
                  <span className="bu-upload-icon">☁️</span>
                  <p>CSV फाइल येथे टाका किंवा क्लिक करा</p>
                </div>
              )}
            </div>

            {error && <p className="bu-error">{error}</p>}

            <button
              className="bu-upload-btn"
              onClick={isMilkEntry ? handlePreview : handleUploadMembers}
              disabled={!file || loading}
            >
              {loading ? "..." : isMilkEntry ? "पूर्वावलोकन करा" : "अपलोड करा"}
            </button>
          </>
        )}

        {/* Milk-entry preview: counts + before/after diff, awaiting approval */}
        {preview && (
          <div className="bu-result">
            <div className="bu-result-summary bu-result-summary--4">
              <div className="bu-result-stat bu-result-total">
                <span>एकूण</span>
                <strong>{preview.total}</strong>
              </div>
              <div className="bu-result-stat bu-result-insert">
                <span>नवीन</span>
                <strong>{preview.toInsert}</strong>
              </div>
              <div className="bu-result-stat bu-result-update">
                <span>बदलणार</span>
                <strong>{preview.toUpdate}</strong>
              </div>
              <div className="bu-result-stat bu-result-fail">
                <span>अयशस्वी</span>
                <strong>{preview.toFail}</strong>
              </div>
            </div>

            {preview.hasDuplicates && (
              <p className="bu-error bu-dupe-warning">
                ⚠️ CSV मध्ये एकाच सभासदाची, एकाच तारखेची व सत्राची डुप्लिकेट नोंद आढळली — त्या ओळी खाली लाल रंगात दाखवल्या आहेत.
                कृपया CSV फाइल दुरुस्त करून पुन्हा अपलोड करा. डुप्लिकेट असेपर्यंत जतन करता येणार नाही.
              </p>
            )}

            <div className="bu-preview-table-wrap">
              <table className="bu-preview-table">
                <thead>
                  <tr>
                    <th>क्र.</th>
                    <th>नाव</th>
                    <th>तारीख</th>
                    <th>सत्र</th>
                    <th>स्थिती</th>
                    <th>लि./फॅट/SNF (आधी → नंतर)</th>
                    <th>रक्कम (आधी → नंतर)</th>
                    <th>फरक (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const diff = r.action === "failed" ? null : rowDiff(r);
                    return (
                      <tr key={i} className={ACTION_LABEL[r.action]?.cls}>
                        <td>{r.dairy_id ?? "-"}</td>
                        <td className="bu-name-cell">{r.name || "-"}</td>
                        <td>{r.date ?? "-"}</td>
                        <td>{r.shift ?? "-"}</td>
                        <td><span className={`bu-action-pill ${ACTION_LABEL[r.action]?.cls}`}>{ACTION_LABEL[r.action]?.text}</span></td>
                        <td>
                          {r.action === "failed" ? r.reason : r.action === "update"
                            ? `${fmt(r.before.qty)}/${fmt(r.before.fat)}/${fmt(r.before.snf)} → ${fmt(r.after.qty)}/${fmt(r.after.fat)}/${fmt(r.after.snf)}`
                            : `${fmt(r.after.qty)}/${fmt(r.after.fat)}/${fmt(r.after.snf)}`}
                        </td>
                        <td>
                          {r.action === "failed" ? "-" : r.action === "update"
                            ? `₹${fmt(r.before.total_rate)} → ₹${fmt(r.after.total_rate)}`
                            : `₹${fmt(r.after.total_rate)}`}
                        </td>
                        <td className={diff > 0 ? "bu-diff-pos" : diff < 0 ? "bu-diff-neg" : ""}>
                          {diff == null ? "-" : `${diff > 0 ? "+" : ""}₹${fmt(diff)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {error && <p className="bu-error">{error}</p>}

            <div className="bu-preview-actions">
              <button
                className="bu-upload-btn bu-approve-btn"
                onClick={handleApprove}
                disabled={loading || preview.hasDuplicates}
                title={preview.hasDuplicates ? "आधी CSV मधील डुप्लिकेट नोंदी दुरुस्त करा" : undefined}
              >
                {loading ? "जतन होत आहे..." : preview.hasDuplicates ? "डुप्लिकेट दुरुस्त करा" : "मंजूर करा आणि जतन करा"}
              </button>
              <button className="bu-cancel-btn" onClick={handleReset} disabled={loading}>
                रद्द करा
              </button>
            </div>
          </div>
        )}

        {/* Final result (both modes) */}
        {result && (
          <div className="bu-result">
            <div className={`bu-result-summary ${isMilkEntry ? "bu-result-summary--4" : ""}`}>
              <div className="bu-result-stat bu-result-total">
                <span>एकूण</span>
                <strong>{result.total}</strong>
              </div>
              <div className="bu-result-stat bu-result-success">
                <span>{isMilkEntry ? "नवीन" : "यशस्वी"}</span>
                <strong>{result.inserted}</strong>
              </div>
              {isMilkEntry && (
                <div className="bu-result-stat bu-result-update">
                  <span>बदलले</span>
                  <strong>{result.updated || 0}</strong>
                </div>
              )}
              <div className="bu-result-stat bu-result-fail">
                <span>अयशस्वी</span>
                <strong>{result.failed?.length || 0}</strong>
              </div>
            </div>

            {result.failed?.length > 0 && (
              <div className="bu-failed-list">
                <p className="bu-failed-title">अयशस्वी नोंदी:</p>
                {result.failed.map((f, i) => (
                  <div key={i} className="bu-failed-item">
                    <span className="bu-failed-reason">{f.reason}</span>
                    <span className="bu-failed-row">{JSON.stringify(f.row)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUpload;
